/**
 * YUMMY Declaration Page
 * (Your Ultimate Most Meaningful Yet)
 * 
 * A tool for members to declare who they are, what matters most, 
 * and the life they're committed to building.
 * Includes core values, life domains, legacy statement, and 10-year vision.
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useCelebration } from '@/context/CelebrationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, ArrowRight, Loader2, Sparkles, Briefcase, Heart, HeartPulse,
  Coins, Users, CheckCircle, Edit, Target, MapPin, Compass, Wand2, RefreshCw,
  Plus, Trash2, Calendar, Flag, UserPlus, GripVertical, Clock, AlertTriangle,
  Bell, BellOff, Mail, Phone, Mic
} from 'lucide-react';
import VoiceInput from '@/components/voice/VoiceInput';

const API_URL = window.location.origin;

// Expandable Text Component for long content
const ExpandableText = ({ text, maxLength = 150, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!text) return null;
  
  const shouldTruncate = text.length > maxLength;
  const displayText = shouldTruncate && !isExpanded 
    ? text.substring(0, maxLength) + '...' 
    : text;
  
  return (
    <div className={className}>
      <p className="leading-relaxed whitespace-pre-wrap">
        {displayText}
      </p>
      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-sm text-terracotta hover:underline mt-2 font-medium"
        >
          {isExpanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
};

const DOMAIN_ICONS = {
  career: Briefcase,
  relationships: Heart,
  health: HeartPulse,
  wealth: Coins,
  spirituality: Sparkles,
  community: Users
};

const DOMAIN_COLORS = {
  career: 'amber',
  relationships: 'rose',
  health: 'green',
  wealth: 'yellow',
  spirituality: 'purple',
  community: 'blue'
};

const PRIORITY_CONFIG = {
  high: { label: 'High', color: 'bg-red-100 text-red-700 border-red-200', icon: '🔥' },
  medium: { label: 'Medium', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: '⚡' },
  low: { label: 'Low', color: 'bg-green-100 text-green-700 border-green-200', icon: '🌱' }
};

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'bg-gray-100 text-gray-600' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  paused: { label: 'Paused', color: 'bg-amber-100 text-amber-700' }
};

const LegacyVisionPage = () => {
  const navigate = useNavigate();
  const { celebrate, grandCelebrate, epicCelebrate } = useCelebration();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [vision, setVision] = useState(null);
  const [domains, setDomains] = useState([]);
  const [coreValues, setCoreValues] = useState([]);
  const [prompts, setPrompts] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialog states
  const [showValuesDialog, setShowValuesDialog] = useState(false);
  const [showLegacyDialog, setShowLegacyDialog] = useState(false);
  const [showVisionDialog, setShowVisionDialog] = useState(false);
  const [showDomainDialog, setShowDomainDialog] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  
  // Milestone states
  const [milestones, setMilestones] = useState([]);
  const [milestoneStats, setMilestoneStats] = useState({});
  const [showMilestoneDialog, setShowMilestoneDialog] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [upcomingMilestones, setUpcomingMilestones] = useState({});
  const [reminderPreferences, setReminderPreferences] = useState({
    enabled: true,
    email_reminders: false,
    sms_reminders: false,
    in_app_reminders: true,
    stagnant_nudge: true
  });
  const [milestoneForm, setMilestoneForm] = useState({
    goal: '',
    domain_id: '',
    target_date: '',
    priority: 'medium',
    is_delegatable: false,
    who_can_help: '',
    notes: ''
  });
  
  // Form states
  const [selectedValues, setSelectedValues] = useState([]);
  const [legacyStatement, setLegacyStatement] = useState('');
  const [tenYearVision, setTenYearVision] = useState('');
  const [domainVision, setDomainVision] = useState('');
  const [domainGoals, setDomainGoals] = useState(['', '', '']);
  
  // SHE Suggestion dialog state
  const [showSheSuggestion, setShowSheSuggestion] = useState(false);
  const [sheSuggestion, setSheSuggestion] = useState('');
  const [suggestionType, setSuggestionType] = useState(''); // 'legacy' or 'vision'
  
  // YUMMY Declaration state
  const [showDeclarationDialog, setShowDeclarationDialog] = useState(false);
  const [yummyDeclaration, setYummyDeclaration] = useState('');
  const [generatingDeclaration, setGeneratingDeclaration] = useState(false);
  const [editingDeclaration, setEditingDeclaration] = useState(false);
  const [addedToDashboard, setAddedToDashboard] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [domainsRes, valuesRes, promptsRes, visionRes] = await Promise.all([
        axios.get(`${API_URL}/api/legacy-vision/domains`),
        axios.get(`${API_URL}/api/legacy-vision/values`),
        axios.get(`${API_URL}/api/legacy-vision/prompts`),
        axios.get(`${API_URL}/api/legacy-vision/mine`, { headers })
      ]);
      
      setDomains(domainsRes.data.domains || []);
      setCoreValues(valuesRes.data.values || []);
      setPrompts(promptsRes.data);
      setVision(visionRes.data.vision);
      
      // Pre-fill form states if vision exists
      if (visionRes.data.vision) {
        setSelectedValues(visionRes.data.vision.core_values || []);
        setLegacyStatement(visionRes.data.vision.legacy_statement?.statement || '');
        setTenYearVision(visionRes.data.vision.ten_year_vision?.vision || '');
      }
      
      // Also fetch milestones
      await fetchMilestones();
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMilestones = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/legacy-vision/milestones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMilestones(response.data.milestones || []);
      setMilestoneStats(response.data.stats || {});
      
      // Also fetch upcoming milestones and reminder preferences
      await fetchUpcomingMilestones();
      await fetchReminderPreferences();
    } catch (error) {
      console.error('Failed to fetch milestones:', error);
    }
  };

  const fetchUpcomingMilestones = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/legacy-vision/milestones/upcoming`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpcomingMilestones(response.data || {});
    } catch (error) {
      console.error('Failed to fetch upcoming milestones:', error);
    }
  };

  const fetchReminderPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/legacy-vision/milestones/reminder-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminderPreferences(response.data || {});
    } catch (error) {
      console.error('Failed to fetch reminder preferences:', error);
    }
  };

  const updateReminderPreference = async (key, value) => {
    try {
      const token = localStorage.getItem('token');
      const newPrefs = { ...reminderPreferences, [key]: value };
      setReminderPreferences(newPrefs);
      
      await axios.put(
        `${API_URL}/api/legacy-vision/milestones/reminder-preferences`,
        newPrefs,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reminder preference updated');
    } catch (error) {
      toast.error('Failed to update preference');
    }
  };

  const saveMilestone = async () => {
    if (!milestoneForm.goal.trim()) {
      toast.error('Please enter a goal');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingMilestone) {
        // Update existing
        await axios.put(
          `${API_URL}/api/legacy-vision/milestones/${editingMilestone.id}`,
          milestoneForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Milestone updated!');
      } else {
        // Create new
        await axios.post(
          `${API_URL}/api/legacy-vision/milestones`,
          milestoneForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Milestone created!');
        celebrate('confetti', 'New milestone set!');
      }
      
      await fetchMilestones();
      closeMilestoneDialog();
    } catch (error) {
      toast.error('Failed to save milestone');
    } finally {
      setSaving(false);
    }
  };

  const deleteMilestone = async (milestoneId) => {
    if (!window.confirm('Are you sure you want to delete this milestone?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/legacy-vision/milestones/${milestoneId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Milestone deleted');
      await fetchMilestones();
    } catch (error) {
      toast.error('Failed to delete milestone');
    }
  };

  const updateMilestoneStatus = async (milestoneId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/legacy-vision/milestones/${milestoneId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (newStatus === 'completed') {
        celebrate('sparkle', 'Milestone completed! 🎉');
      }
      
      await fetchMilestones();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const updateMilestoneProgress = async (milestoneId, progressPercent) => {
    try {
      const token = localStorage.getItem('token');
      
      // Determine status based on progress
      let newStatus = 'not_started';
      if (progressPercent === 100) {
        newStatus = 'completed';
      } else if (progressPercent > 0) {
        newStatus = 'in_progress';
      }
      
      await axios.put(
        `${API_URL}/api/legacy-vision/milestones/${milestoneId}`,
        { progress_percent: progressPercent, status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (progressPercent === 100) {
        celebrate('sparkle', 'Milestone completed! 🎉');
      } else if (progressPercent >= 75) {
        toast.success('Almost there! Keep going! 💪🏾');
      } else if (progressPercent >= 50) {
        toast.success('Halfway there! 🌟');
      }
      
      await fetchMilestones();
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const importGoalsAsMilestones = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/legacy-vision/milestones/import`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.milestones?.length > 0) {
        toast.success(`Imported ${response.data.milestones.length} goals as milestones!`);
        celebrate('confetti', 'Goals imported!');
        await fetchMilestones();
      } else {
        toast.info('All goals have already been imported');
      }
    } catch (error) {
      toast.error('Failed to import goals');
    }
  };

  const openMilestoneDialog = (milestone = null) => {
    if (milestone) {
      setEditingMilestone(milestone);
      setMilestoneForm({
        goal: milestone.goal || '',
        domain_id: milestone.domain_id || '',
        target_date: milestone.target_date ? milestone.target_date.split('T')[0] : '',
        priority: milestone.priority || 'medium',
        is_delegatable: milestone.is_delegatable || false,
        who_can_help: milestone.who_can_help || '',
        notes: milestone.notes || ''
      });
    } else {
      setEditingMilestone(null);
      setMilestoneForm({
        goal: '',
        domain_id: '',
        target_date: '',
        priority: 'medium',
        is_delegatable: false,
        who_can_help: '',
        notes: ''
      });
    }
    setShowMilestoneDialog(true);
  };

  const closeMilestoneDialog = () => {
    setShowMilestoneDialog(false);
    setEditingMilestone(null);
    setMilestoneForm({
      goal: '',
      domain_id: '',
      target_date: '',
      priority: 'medium',
      is_delegatable: false,
      who_can_help: '',
      notes: ''
    });
  };

  const saveSection = async (section, sectionData) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/legacy-vision/update`,
        { section, data: sectionData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Celebrate based on section
      if (section === 'core_values') {
        celebrate('confetti', 'Your values are set! These will guide your journey.');
      } else if (section === 'legacy_statement') {
        grandCelebrate('sparkle');
        toast.success('Your legacy statement is powerful!');
      } else if (section === 'ten_year_vision') {
        grandCelebrate('confetti', 'milestone');
        toast.success('Your vision is alive!');
      }
      
      toast.success(response.data.message);
      await fetchData();
      
      // Close dialogs
      setShowValuesDialog(false);
      setShowLegacyDialog(false);
      setShowVisionDialog(false);
      setShowDomainDialog(false);
      
      // Check if all sections are complete for epic celebration
      setTimeout(() => {
        checkCompletion();
      }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const checkCompletion = () => {
    const hasValues = vision?.core_values?.length === 5;
    const hasLegacy = vision?.legacy_statement?.statement;
    const hasVision = vision?.ten_year_vision?.vision;
    const hasDomains = Object.keys(vision?.domain_visions || {}).length >= 3; // At least 3 domains
    
    // Only celebrate when legacy statement, values, vision AND at least 3 domains are complete
    if (hasValues && hasLegacy && hasVision && hasDomains && !showCompletionDialog) {
      epicCelebrate("Your YUMMY Declaration is complete! You've declared your future! 💎✨");
      setShowCompletionDialog(true);
    }
  };

  const askSHEToRewrite = async (type) => {
    setAiLoading(true);
    try {
      const token = localStorage.getItem('token');
      const textToRewrite = type === 'legacy' ? legacyStatement : tenYearVision;
      const statementType = type === 'legacy' ? 'legacy statement' : '10-year vision';
      
      // Use the SHE chat endpoint for rewriting - it gives direct conversational responses
      const response = await axios.post(
        `${API_URL}/api/chat/she`,
        { 
          message: `Please help me refine and elevate this ${statementType}. Rewrite it to be more powerful, clear, and inspiring while keeping my voice and intentions. Just give me the rewritten version without any preamble. Here's what I wrote: "${textToRewrite}"`,
          session_id: `legacy-vision-${Date.now()}`,
          page_context: type === 'legacy' ? 'legacy_statement' : 'ten_year_vision'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Extract the refined text from SHE's response
      const sheResponse = response.data.response || '';
      
      if (!sheResponse) {
        toast.info('SHE is reflecting. Please try again in a moment.');
        return;
      }
      
      // Show the suggestion in a dialog for user to review and edit
      setSuggestionType(type);
      setSheSuggestion(sheResponse);
      setShowSheSuggestion(true);
      toast.success('SHE has a suggestion for you! Review and edit as you like.');
      
    } catch (error) {
      console.error('SHE refinement error:', error);
      // Handle error message properly - it might be an object or array
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(`Unable to get SHE's help right now: ${errorMessage}`);
    } finally {
      setAiLoading(false);
    }
  };
  
  // Accept SHE's suggestion (with any edits the user made)
  const acceptSheSuggestion = () => {
    if (suggestionType === 'legacy') {
      setLegacyStatement(sheSuggestion);
    } else if (suggestionType === 'vision') {
      setTenYearVision(sheSuggestion);
    }
    setShowSheSuggestion(false);
    setSheSuggestion('');
    setSuggestionType('');
    toast.success('Suggestion applied! Feel free to continue editing.');
  };

  // Generate YUMMY Declaration based on all answers
  const generateYummyDeclaration = async () => {
    setGeneratingDeclaration(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/legacy-vision/generate-declaration`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setYummyDeclaration(response.data.declaration || '');
      setAddedToDashboard(response.data.added_to_dashboard || false);
    } catch (error) {
      console.error('Error generating declaration:', error);
      // Fallback: create a simple declaration from available data
      const values = vision?.core_values?.join(', ') || 'my values';
      const legacy = vision?.legacy_statement || 'making a meaningful impact';
      const fallbackDeclaration = `I am a woman who stands for ${values}. My legacy is ${legacy}. I am committed to living my most meaningful life—not just doing what's good, but doing what's YUMMY: creating freedom and lasting impact in everything I do.`;
      setYummyDeclaration(fallbackDeclaration);
      toast.error('Could not generate AI declaration. Using template instead.');
    } finally {
      setGeneratingDeclaration(false);
    }
  };

  // Save YUMMY Declaration
  const saveDeclaration = async (addToDashboard = false) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/legacy-vision/save-declaration`,
        { 
          declaration: yummyDeclaration,
          add_to_dashboard: addToDashboard 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (addToDashboard) {
        setAddedToDashboard(true);
        toast.success('YUMMY Declaration saved and added to your dashboard!');
      } else {
        toast.success('YUMMY Declaration saved!');
      }
      setEditingDeclaration(false);
    } catch (error) {
      console.error('Error saving declaration:', error);
      toast.error('Failed to save declaration');
    }
  };

  const openDomainDialog = (domain) => {
    setSelectedDomain(domain);
    const existing = vision?.domain_visions?.[domain.id];
    setDomainVision(existing?.vision || '');
    setDomainGoals(existing?.goals || ['', '', '']);
    setShowDomainDialog(true);
  };

  const toggleValue = (value) => {
    if (selectedValues.includes(value)) {
      setSelectedValues(selectedValues.filter(v => v !== value));
    } else if (selectedValues.length < 5) {
      setSelectedValues([...selectedValues, value]);
    } else {
      toast.error('You can only select 5 core values');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
        </div>
      </DashboardLayout>
    );
  }

  const completionPercent = vision?.completion_percent || 0;
  const isComplete = completionPercent === 100;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="legacy-vision-page">
        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-600 hover:text-forest mb-4">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-terracotta to-amber-500 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-heading text-forest">YUMMY Declaration</h1>
              <p className="text-gray-600">Declare who you are, what matters most, and the life you are committed to building.</p>
              <p className="text-sm text-terracotta font-medium mt-1">Your Ultimate Most Meaningful Yet</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Completion</p>
              <p className="text-xl font-heading text-forest">{completionPercent}%</p>
            </div>
          </div>
          
          <Progress value={completionPercent} className="h-2" />
          
          {isComplete && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Your YUMMY Declaration is Complete!</p>
                  <p className="text-sm text-green-600">You've declared the life you're committed to building.</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/challenges')}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Continue to Challenges <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="domains">Life Domains</TabsTrigger>
            <TabsTrigger value="milestones">Milestones</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Core Values Card */}
            <Card className="card-base">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-heading text-forest">Core Values</CardTitle>
                    <p className="text-sm text-gray-500">Your top 5 guiding principles</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowValuesDialog(true)}
                  data-testid="edit-values-btn"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  {vision?.core_values?.length ? 'Edit' : 'Set Values'}
                </Button>
              </CardHeader>
              <CardContent>
                {vision?.core_values?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {vision.core_values.map(value => (
                      <Badge key={value} className="bg-purple-100 text-purple-700 px-4 py-2 text-sm">
                        {value}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Select your top 5 core values to guide your vision.</p>
                )}
              </CardContent>
            </Card>

            {/* Legacy Statement Card */}
            <Card className="card-base">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Target className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-heading text-forest">Legacy Statement</CardTitle>
                    <p className="text-sm text-gray-500">How do you want to be remembered?</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowLegacyDialog(true)}
                  data-testid="edit-legacy-btn"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  {vision?.legacy_statement ? 'Edit' : 'Create'}
                </Button>
              </CardHeader>
              <CardContent>
                {vision?.legacy_statement?.statement ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                    <ExpandableText 
                      text={`"I want to be remembered as someone who ${vision.legacy_statement.statement}"`}
                      maxLength={200}
                      className="text-lg text-amber-900 italic"
                    />
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Define how you want to be remembered and the lasting impact you want to make.</p>
                )}
              </CardContent>
            </Card>

            {/* 10-Year Vision Card */}
            <Card className="card-base">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-heading text-forest">10-Year Vision</CardTitle>
                    <p className="text-sm text-gray-500">A day in your future life</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowVisionDialog(true)}
                  data-testid="edit-vision-btn"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  {vision?.ten_year_vision ? 'Edit' : 'Create'}
                </Button>
              </CardHeader>
              <CardContent>
                {vision?.ten_year_vision?.vision ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <ExpandableText 
                      text={vision.ten_year_vision.vision}
                      maxLength={300}
                      className="text-blue-900"
                    />
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Imagine and describe a day in your life 10 years from now.</p>
                )}
              </CardContent>
            </Card>

            {/* Next Steps - Enhanced with navigation */}
            {!isComplete && (
              <Card className="card-base bg-gradient-to-r from-forest/5 to-terracotta/5">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg text-forest mb-3">What's Next?</h3>
                  <div className="space-y-3">
                    {!vision?.core_values?.length && (
                      <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <span className="h-6 w-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold">1</span>
                          Select your 5 core values
                        </p>
                        <Button size="sm" variant="outline" onClick={() => setShowValuesDialog(true)}>
                          Start
                        </Button>
                      </div>
                    )}
                    {vision?.core_values?.length > 0 && !vision?.legacy_statement && (
                      <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <span className="h-6 w-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs font-bold">2</span>
                          Write your legacy statement
                        </p>
                        <Button size="sm" variant="outline" onClick={() => setShowLegacyDialog(true)}>
                          Start
                        </Button>
                      </div>
                    )}
                    {vision?.legacy_statement && !vision?.ten_year_vision && (
                      <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <span className="h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">3</span>
                          Describe your 10-year vision
                        </p>
                        <Button size="sm" variant="outline" onClick={() => setShowVisionDialog(true)}>
                          Start
                        </Button>
                      </div>
                    )}
                    {vision?.ten_year_vision && Object.keys(vision?.domain_visions || {}).length < 3 && (
                      <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <span className="h-6 w-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-bold">4</span>
                          Complete at least 3 Life Domains
                        </p>
                        <Button size="sm" variant="outline" onClick={() => setActiveTab('domains')}>
                          Go to Domains <ArrowRight className="h-3 w-3 ml-1" />
                        </Button>
                      </div>
                    )}
                    {Object.keys(vision?.domain_visions || {}).length >= 3 && (
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm text-green-700 flex items-center gap-2">
                          <CheckCircle className="h-5 w-5" />
                          Great progress! Set your milestones to complete your vision map.
                        </p>
                        <Button size="sm" className="bg-forest hover:bg-forest/90" onClick={() => setActiveTab('milestones')}>
                          Set Milestones <ArrowRight className="h-3 w-3 ml-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Life Domains Tab */}
          <TabsContent value="domains">
            <div className="space-y-4">
              {/* YUMMY explanation */}
              <Card className="card-base bg-gradient-to-r from-terracotta/10 to-amber-50 border-terracotta/20">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-6 w-6 text-terracotta flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-forest mb-1">Remember: YUMMY isn't about doing "good" things...</p>
                      <p className="text-sm text-gray-700">
                        It's about doing the <span className="font-semibold text-terracotta">most meaningful things</span> that 
                        ultimately create <span className="font-semibold">freedom and legacy</span>. Focus on what truly lights 
                        you up and moves the needle in your life—not what you "should" do, but what you're <em>called</em> to do.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Progress indicator */}
              <div className="flex items-center justify-between p-3 bg-sand/30 rounded-lg">
                <span className="text-sm text-gray-600">
                  Domains completed: {Object.keys(vision?.domain_visions || {}).length} / {domains.length}
                </span>
                <span className="text-sm text-gray-500">
                  (Complete at least 3 to proceed)
                </span>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                {domains.map(domain => {
                  const Icon = DOMAIN_ICONS[domain.id] || Sparkles;
                  const color = DOMAIN_COLORS[domain.id] || 'gray';
                  const savedDomain = vision?.domain_visions?.[domain.id];
                  
                  return (
                    <Card 
                      key={domain.id} 
                      className={`card-base cursor-pointer hover:shadow-md transition-shadow ${savedDomain ? 'border-green-200 bg-green-50/30' : ''}`}
                      onClick={() => openDomainDialog(domain)}
                      data-testid={`domain-${domain.id}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <div className={`h-12 w-12 rounded-xl bg-${color}-100 flex items-center justify-center shrink-0`}>
                            <Icon className={`h-6 w-6 text-${color}-600`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-forest">{domain.label}</h3>
                              {savedDomain && (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              )}
                            </div>
                            {savedDomain ? (
                              <div className="space-y-2">
                                <p className="text-sm text-gray-700 line-clamp-2">{savedDomain.vision}</p>
                                {savedDomain.goals?.length > 0 && (
                                  <div className="text-xs text-gray-500">
                                    <span className="font-medium">Goals:</span>
                                    <ul className="mt-1 space-y-0.5">
                                      {savedDomain.goals.slice(0, 2).map((goal, i) => (
                                        <li key={goal} className="truncate">• {goal}</li>
                                      ))}
                                      {savedDomain.goals.length > 2 && (
                                        <li className="text-gray-400">+{savedDomain.goals.length - 2} more</li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                                <Button size="sm" variant="ghost" className="text-xs p-0 h-auto text-terracotta hover:text-terracotta/80">
                                  <Edit className="h-3 w-3 mr-1" /> Edit
                                </Button>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">{domain.description}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              {/* Navigation to Milestones */}
              {Object.keys(vision?.domain_visions || {}).length >= 3 && (
                <Card className="card-base bg-gradient-to-r from-forest/5 to-terracotta/5">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-forest">Ready for Milestones!</p>
                      <p className="text-sm text-gray-600">Set SMART goals and timelines for your vision.</p>
                    </div>
                    <Button className="bg-forest hover:bg-forest/90" onClick={() => setActiveTab('milestones')}>
                      Set Milestones <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Milestones Tab - Enhanced with SMART Goals */}
          <TabsContent value="milestones">
            {(!vision?.legacy_statement || !vision?.ten_year_vision) ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <Target className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-heading text-forest mb-2">Annual Milestones</h3>
                  <p className="text-gray-600 mb-4">
                    Set your goals and milestones for each year of your journey.
                  </p>
                  <p className="text-sm text-gray-500">
                    Complete your Legacy Statement and 10-Year Vision first to unlock milestones.
                  </p>
                  <Button className="mt-4" variant="outline" onClick={() => setActiveTab('overview')}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Go Back
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Summary of Goals from Vision & Domains */}
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      Your Vision Goals Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Legacy & Vision Summary */}
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <h4 className="font-medium text-amber-800 mb-2">Your Legacy Statement</h4>
                      <p className="text-sm text-amber-700 italic">"{vision?.legacy_statement?.statement}"</p>
                    </div>
                    
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-800 mb-2">10-Year Vision</h4>
                      <p className="text-sm text-blue-700">{vision?.ten_year_vision?.vision}</p>
                    </div>
                    
                    {/* Domain Goals */}
                    {Object.entries(vision?.domain_visions || {}).length > 0 && (
                      <div className="space-y-3">
                        <h4 className="font-medium text-forest">Goals by Life Domain</h4>
                        {Object.entries(vision?.domain_visions || {}).map(([domainId, domainData]) => {
                          const domain = domains.find(d => d.id === domainId);
                          const Icon = DOMAIN_ICONS[domainId] || Sparkles;
                          return (
                            <div key={domainId} className="p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Icon className="h-4 w-4 text-forest" />
                                <span className="font-medium text-sm">{domain?.label || domainId}</span>
                              </div>
                              {domainData.goals?.length > 0 ? (
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {domainData.goals.map((goal, i) => (
                                    <li key={goal} className="flex items-start gap-2">
                                      <span className="text-terracotta">•</span>
                                      {goal}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-sm text-gray-400 italic">No specific goals set</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Milestone Stats */}
                {milestones.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-blue-50 rounded-xl text-center">
                      <p className="text-2xl font-bold text-blue-600">{milestoneStats.total || 0}</p>
                      <p className="text-xs text-blue-600">Total Goals</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-xl text-center">
                      <p className="text-2xl font-bold text-amber-600">{milestoneStats.in_progress || 0}</p>
                      <p className="text-xs text-amber-600">In Progress</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-xl text-center">
                      <p className="text-2xl font-bold text-green-600">{milestoneStats.completed || 0}</p>
                      <p className="text-xs text-green-600">Completed</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-xl text-center">
                      <p className="text-2xl font-bold text-red-600">{milestoneStats.overdue || 0}</p>
                      <p className="text-xs text-red-600">Overdue</p>
                    </div>
                  </div>
                )}

                {/* Upcoming Milestones & Reminders */}
                {milestones.length > 0 && (
                  <Card className="card-base" data-testid="upcoming-milestones-card">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                          <Bell className="h-5 w-5" />
                          Upcoming Milestones & Reminders
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Reminders</span>
                          <Switch
                            checked={reminderPreferences.enabled}
                            onCheckedChange={(checked) => updateReminderPreference('enabled', checked)}
                            data-testid="reminder-toggle"
                          />
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* This Week */}
                      {upcomingMilestones.this_week?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-amber-700 mb-2 flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Due This Week
                          </h4>
                          <div className="space-y-2">
                            {upcomingMilestones.this_week.map((m) => (
                              <div 
                                key={m.id} 
                                className={`p-3 rounded-lg border ${
                                  m.is_overdue 
                                    ? 'bg-red-50 border-red-200' 
                                    : 'bg-amber-50 border-amber-200'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className={`text-sm font-medium ${m.is_overdue ? 'text-red-700' : 'text-amber-900'}`}>
                                    {m.goal}
                                  </span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    m.is_overdue 
                                      ? 'bg-red-200 text-red-700' 
                                      : m.days_until <= 3 
                                      ? 'bg-orange-200 text-orange-700'
                                      : 'bg-amber-200 text-amber-700'
                                  }`}>
                                    {m.is_overdue 
                                      ? `${Math.abs(m.days_until)} days overdue` 
                                      : m.days_until === 0 
                                      ? 'Due today!'
                                      : m.days_until === 1 
                                      ? 'Due tomorrow' 
                                      : `${m.days_until} days left`}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* This Month */}
                      {upcomingMilestones.this_month?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-blue-700 mb-2 flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Coming Up This Month
                          </h4>
                          <div className="space-y-2">
                            {upcomingMilestones.this_month.slice(0, 3).map((m) => (
                              <div key={m.id} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-blue-900">{m.goal}</span>
                                  <span className="text-xs bg-blue-200 text-blue-700 px-2 py-0.5 rounded-full">
                                    {m.days_until} days
                                  </span>
                                </div>
                              </div>
                            ))}
                            {upcomingMilestones.this_month.length > 3 && (
                              <p className="text-xs text-gray-500 text-center">
                                +{upcomingMilestones.this_month.length - 3} more this month
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* No Deadlines */}
                      {upcomingMilestones.no_deadline?.length > 0 && (
                        <div className="pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {upcomingMilestones.no_deadline.length} milestone{upcomingMilestones.no_deadline.length > 1 ? 's' : ''} without deadlines
                          </p>
                        </div>
                      )}

                      {/* Empty State */}
                      {!upcomingMilestones.this_week?.length && 
                       !upcomingMilestones.this_month?.length && (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No upcoming deadlines. Add target dates to your milestones to get reminders!
                        </p>
                      )}

                      {/* Reminder Settings */}
                      {reminderPreferences.enabled && (
                        <div className="pt-4 border-t border-gray-100">
                          <p className="text-xs font-medium text-gray-500 mb-3">Reminder Settings</p>
                          <div className="space-y-2">
                            <label className="flex items-center justify-between cursor-pointer">
                              <span className="text-sm text-gray-600 flex items-center gap-2">
                                <Bell className="h-4 w-4" />
                                In-app notifications
                              </span>
                              <Switch
                                checked={reminderPreferences.in_app_reminders}
                                onCheckedChange={(checked) => updateReminderPreference('in_app_reminders', checked)}
                                data-testid="in-app-reminder-toggle"
                              />
                            </label>
                            <label className="flex items-center justify-between cursor-pointer">
                              <span className="text-sm text-gray-600 flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                Email reminders
                              </span>
                              <Switch
                                checked={reminderPreferences.email_reminders}
                                onCheckedChange={(checked) => updateReminderPreference('email_reminders', checked)}
                                data-testid="email-reminder-toggle"
                              />
                            </label>
                            <label className="flex items-center justify-between cursor-pointer">
                              <span className="text-sm text-gray-600 flex items-center gap-2">
                                <Phone className="h-4 w-4" />
                                Text (SMS) reminders
                              </span>
                              <Switch
                                checked={reminderPreferences.sms_reminders}
                                onCheckedChange={(checked) => updateReminderPreference('sms_reminders', checked)}
                                data-testid="sms-reminder-toggle"
                              />
                            </label>
                            <label className="flex items-center justify-between cursor-pointer">
                              <span className="text-sm text-gray-600 flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                Nudge for stagnant milestones (14+ days)
                              </span>
                              <Switch
                                checked={reminderPreferences.stagnant_nudge}
                                onCheckedChange={(checked) => updateReminderPreference('stagnant_nudge', checked)}
                                data-testid="stagnant-nudge-toggle"
                              />
                            </label>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* SMART Goals Info */}
                <Card className="card-base">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                      <Compass className="h-5 w-5" />
                      SMART Milestones
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-3">
                      Turn your vision into actionable milestones using the SMART framework:
                    </p>
                    <div className="grid grid-cols-5 gap-2 mb-4 text-center text-xs">
                      <div className="p-2 bg-purple-50 rounded-lg">
                        <span className="font-bold text-purple-600">S</span>pecific
                      </div>
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <span className="font-bold text-blue-600">M</span>easurable
                      </div>
                      <div className="p-2 bg-green-50 rounded-lg">
                        <span className="font-bold text-green-600">A</span>chievable
                      </div>
                      <div className="p-2 bg-amber-50 rounded-lg">
                        <span className="font-bold text-amber-600">R</span>elevant
                      </div>
                      <div className="p-2 bg-rose-50 rounded-lg">
                        <span className="font-bold text-rose-600">T</span>ime-bound
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={() => openMilestoneDialog()}
                        className="bg-forest hover:bg-forest/90 text-white"
                        data-testid="add-milestone-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" /> Add Milestone
                      </Button>
                      
                      {Object.keys(vision?.domain_visions || {}).length > 0 && (
                        <Button
                          onClick={importGoalsAsMilestones}
                          variant="outline"
                          className="border-terracotta text-terracotta hover:bg-terracotta/10"
                          data-testid="import-goals-btn"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" /> Import Goals from Domains
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Milestones List */}
                {milestones.length === 0 ? (
                  <Card className="card-base">
                    <CardContent className="p-8 text-center">
                      <Target className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="font-heading text-lg text-forest mb-2">No Milestones Yet</h3>
                      <p className="text-sm text-gray-500 mb-4">
                        Start by adding your first milestone or import goals from your Life Domains.
                      </p>
                      <Button
                        onClick={() => openMilestoneDialog()}
                        className="bg-forest hover:bg-forest/90 text-white"
                      >
                        <Plus className="h-4 w-4 mr-2" /> Create Your First Milestone
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3" data-testid="milestones-list">
                    {milestones.map((milestone) => {
                      const domain = domains.find(d => d.id === milestone.domain_id);
                      const DomainIcon = milestone.domain_id ? DOMAIN_ICONS[milestone.domain_id] : Target;
                      const isOverdue = milestone.target_date && 
                        new Date(milestone.target_date) < new Date() && 
                        milestone.status !== 'completed';
                      
                      return (
                        <Card 
                          key={milestone.id} 
                          className={`card-base transition-all ${
                            milestone.status === 'completed' ? 'bg-green-50/50 border-green-200' : 
                            isOverdue ? 'bg-red-50/50 border-red-200' : ''
                          }`}
                          data-testid={`milestone-${milestone.id}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              {/* Status Checkbox */}
                              <button
                                onClick={() => updateMilestoneStatus(
                                  milestone.id, 
                                  milestone.status === 'completed' ? 'not_started' : 'completed'
                                )}
                                className={`mt-1 flex-shrink-0 h-6 w-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                  milestone.status === 'completed'
                                    ? 'bg-green-500 border-green-500 text-white'
                                    : 'border-gray-300 hover:border-green-400'
                                }`}
                                data-testid={`milestone-toggle-${milestone.id}`}
                              >
                                {milestone.status === 'completed' && <CheckCircle className="h-4 w-4" />}
                              </button>
                              
                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1">
                                    <p className={`font-medium text-forest ${
                                      milestone.status === 'completed' ? 'line-through text-gray-500' : ''
                                    }`}>
                                      {milestone.goal}
                                    </p>
                                    
                                    {/* Meta info */}
                                    <div className="flex flex-wrap items-center gap-2 mt-2">
                                      {/* Priority Badge */}
                                      <span className={`text-xs px-2 py-0.5 rounded-full border ${PRIORITY_CONFIG[milestone.priority]?.color}`}>
                                        {PRIORITY_CONFIG[milestone.priority]?.icon} {PRIORITY_CONFIG[milestone.priority]?.label}
                                      </span>
                                      
                                      {/* Status Badge */}
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CONFIG[milestone.status]?.color}`}>
                                        {STATUS_CONFIG[milestone.status]?.label}
                                      </span>
                                      
                                      {/* Domain */}
                                      {domain && (
                                        <span className="text-xs text-gray-500 flex items-center gap-1">
                                          <DomainIcon className="h-3 w-3" />
                                          {domain.label}
                                        </span>
                                      )}
                                      
                                      {/* Target Date */}
                                      {milestone.target_date && (
                                        <span className={`text-xs flex items-center gap-1 ${
                                          isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'
                                        }`}>
                                          {isOverdue && <AlertTriangle className="h-3 w-3" />}
                                          <Calendar className="h-3 w-3" />
                                          {new Date(milestone.target_date).toLocaleDateString()}
                                        </span>
                                      )}
                                      
                                      {/* Delegation info */}
                                      {milestone.who_can_help && (
                                        <span className="text-xs text-gray-500 flex items-center gap-1">
                                          <UserPlus className="h-3 w-3" />
                                          {milestone.who_can_help}
                                        </span>
                                      )}
                                    </div>
                                    
                                    {/* Notes */}
                                    {milestone.notes && (
                                      <p className="text-xs text-gray-500 mt-2 italic">
                                        {milestone.notes}
                                      </p>
                                    )}
                                    
                                    {/* Progress Slider */}
                                    {milestone.status !== 'completed' && (
                                      <div className="mt-3 pt-3 border-t border-gray-100">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-xs text-gray-500">Progress</span>
                                          <span className="text-xs font-medium text-forest">
                                            {milestone.progress_percent || 0}%
                                          </span>
                                        </div>
                                        <Slider
                                          value={[milestone.progress_percent || 0]}
                                          onValueChange={(value) => updateMilestoneProgress(milestone.id, value[0])}
                                          max={100}
                                          step={25}
                                          className="w-full"
                                          data-testid={`milestone-progress-${milestone.id}`}
                                        />
                                        <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                                          <span>Not started</span>
                                          <span>25%</span>
                                          <span>50%</span>
                                          <span>75%</span>
                                          <span>Done</span>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Completed Progress Bar */}
                                    {milestone.status === 'completed' && (
                                      <div className="mt-3 pt-3 border-t border-green-100">
                                        <div className="flex items-center gap-2">
                                          <Progress value={100} className="h-1.5 flex-1" />
                                          <span className="text-xs font-medium text-green-600">100%</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Actions */}
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openMilestoneDialog(milestone)}
                                      className="h-8 w-8 p-0"
                                      data-testid={`edit-milestone-${milestone.id}`}
                                    >
                                      <Edit className="h-4 w-4 text-gray-500" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => deleteMilestone(milestone.id)}
                                      className="h-8 w-8 p-0 hover:text-red-600"
                                      data-testid={`delete-milestone-${milestone.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Core Values Dialog */}
        <Dialog open={showValuesDialog} onOpenChange={setShowValuesDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest">
                Select Your Core Values
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-sm text-gray-600 mb-4">
                Choose 5 values that guide your decisions and define who you are. 
                Selected: {selectedValues.length}/5
              </p>
              
              <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
                {coreValues.map(value => (
                  <button
                    key={value}
                    onClick={() => toggleValue(value)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      selectedValues.includes(value)
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowValuesDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => saveSection('core_values', { values: selectedValues })}
                disabled={saving || selectedValues.length !== 5}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Values'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Legacy Statement Dialog - Enhanced */}
        <Dialog open={showLegacyDialog} onOpenChange={setShowLegacyDialog}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest">
                Your Legacy Statement
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <p className="text-sm text-gray-600">
                {prompts.legacy_statement?.description || "Define how you want to be remembered. What lasting impact do you want to leave?"}
              </p>
              
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-sm text-amber-700 font-medium">
                  {prompts.legacy_statement?.prompt || "Think about the lives you want to touch and the change you want to create."}
                </p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-forest">"I want to be remembered as someone who...</label>
                <Textarea
                  placeholder="...empowered women to find their voice, built bridges across communities, and showed up with radical authenticity..."
                  value={legacyStatement}
                  onChange={(e) => setLegacyStatement(e.target.value)}
                  rows={6}
                  className="text-base"
                  spellCheck={true}
                  data-testid="legacy-statement-input"
                />
                <div className="flex items-center justify-between">
                  <VoiceInput 
                    onTranscript={(text) => setLegacyStatement(prev => prev ? `${prev}\n\n${text}` : text)} 
                    disabled={aiLoading}
                  />
                  <span className="text-xs text-gray-400">{legacyStatement.length} characters</span>
                </div>
              </div>
              
              {/* Ask SHE to help */}
              {legacyStatement.trim().length > 20 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => askSHEToRewrite('legacy')}
                  disabled={aiLoading}
                  className="w-full border-terracotta/30 text-terracotta hover:bg-terracotta/5"
                >
                  {aiLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Wand2 className="h-4 w-4 mr-2" />
                  )}
                  Ask SHE to refine this
                </Button>
              )}
              
              {prompts.legacy_statement?.examples && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Examples for inspiration:</p>
                  <ul className="text-xs text-gray-500 space-y-1">
                    {prompts.legacy_statement.examples.slice(0, 3).map((ex, i) => (
                      <li key={ex} className="italic">• {ex}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLegacyDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => saveSection('legacy_statement', { statement: legacyStatement })}
                disabled={saving || !legacyStatement.trim()}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Statement'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 10-Year Vision Dialog - Enhanced */}
        <Dialog open={showVisionDialog} onOpenChange={setShowVisionDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest">
                Your 10-Year Vision
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <p className="text-sm text-gray-600">
                {prompts.ten_year_vision?.description || "Close your eyes and imagine a perfect day 10 years from now. Where are you? What are you doing? Who is with you?"}
              </p>
              
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700 font-medium">
                  {prompts.ten_year_vision?.prompt || "Describe this day in vivid detail - from the moment you wake up to when you go to sleep."}
                </p>
              </div>
              
              <Textarea
                placeholder="I wake up in my beautiful home overlooking... My morning begins with... Throughout the day I... The people in my life include... By evening I feel..."
                value={tenYearVision}
                onChange={(e) => setTenYearVision(e.target.value)}
                rows={10}
                className="text-base"
                spellCheck={true}
                data-testid="ten-year-vision-input"
              />
              
              <div className="flex items-center justify-between">
                <VoiceInput 
                  onTranscript={(text) => setTenYearVision(prev => prev ? `${prev}\n\n${text}` : text)} 
                  disabled={aiLoading}
                />
                <span className="text-xs text-gray-400">{tenYearVision.length} characters</span>
              </div>
              
              {/* Ask SHE to help */}
              {tenYearVision.trim().length > 50 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => askSHEToRewrite('vision')}
                  disabled={aiLoading}
                  className="w-full border-terracotta/30 text-terracotta hover:bg-terracotta/5"
                >
                  {aiLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Wand2 className="h-4 w-4 mr-2" />
                  )}
                  Ask SHE to expand & refine this
                </Button>
              )}
              
              {prompts.ten_year_vision?.guide_questions && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Consider these aspects:</p>
                  <ul className="text-xs text-gray-500 space-y-1 list-disc list-inside">
                    {prompts.ten_year_vision.guide_questions.slice(0, 5).map((q, i) => (
                      <li key={q}>{q}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVisionDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => saveSection('ten_year_vision', { vision: tenYearVision })}
                disabled={saving || !tenYearVision.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Vision'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Domain Vision Dialog */}
        <Dialog open={showDomainDialog} onOpenChange={setShowDomainDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                {selectedDomain && (
                  <>
                    {(() => {
                      const Icon = DOMAIN_ICONS[selectedDomain.id] || Sparkles;
                      return <Icon className="h-5 w-5" />;
                    })()}
                    {selectedDomain.label}
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {selectedDomain && (
              <div className="py-4 space-y-4">
                <p className="text-sm text-gray-600">{selectedDomain.description}</p>
                
                <div>
                  <label className="text-sm font-medium text-forest block mb-2">
                    Your Vision for {selectedDomain.label}
                  </label>
                  <Textarea
                    placeholder={selectedDomain.prompts?.[0] || "Describe your vision..."}
                    value={domainVision}
                    onChange={(e) => setDomainVision(e.target.value)}
                    rows={4}
                    spellCheck={true}
                    data-testid="domain-vision-input"
                  />
                  <div className="mt-2">
                    <VoiceInput 
                      onTranscript={(text) => setDomainVision(prev => prev ? `${prev}\n\n${text}` : text)} 
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-forest block mb-2">
                    3 Goals for This Domain
                  </label>
                  {domainGoals.map((goal, i) => (
                    <input
                      key={`domain-goal-${i}`}
                      type="text"
                      placeholder={`Goal ${i + 1}`}
                      value={goal}
                      onChange={(e) => {
                        const newGoals = [...domainGoals];
                        newGoals[i] = e.target.value;
                        setDomainGoals(newGoals);
                      }}
                      className="w-full mb-2 px-3 py-2 border rounded-lg text-sm"
                    />
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDomainDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => saveSection('domain_vision', {
                  domain_id: selectedDomain?.id,
                  vision: domainVision,
                  goals: domainGoals.filter(g => g.trim())
                })}
                disabled={saving || !domainVision.trim()}
                className="bg-forest hover:bg-forest/90 text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Domain Vision'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Completion Celebration Dialog */}
        <Dialog open={showCompletionDialog} onOpenChange={setShowCompletionDialog}>
          <DialogContent className="sm:max-w-md text-center">
            <div className="py-6">
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-terracotta to-amber-500 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-10 w-10 text-white" />
              </div>
              <h2 className="font-heading text-2xl text-forest mb-2">
                Your YUMMY Declaration is Complete!
              </h2>
              <p className="text-gray-600 mb-6">
                You've declared who you are, what matters most, and the life you're committed to building. Now let's generate your personal YUMMY Declaration statement.
              </p>
              
              <div className="space-y-3">
                <Button 
                  onClick={() => {
                    setShowCompletionDialog(false);
                    setShowDeclarationDialog(true);
                    generateYummyDeclaration();
                  }}
                  className="w-full bg-terracotta hover:bg-terracotta/90 text-white"
                >
                  Generate My YUMMY Declaration
                  <Sparkles className="h-4 w-4 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/challenges')}
                  className="w-full"
                >
                  Skip to Challenges
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* YUMMY Declaration Dialog */}
        <Dialog open={showDeclarationDialog} onOpenChange={setShowDeclarationDialog}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-terracotta" />
                Your YUMMY Declaration
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              {generatingDeclaration ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="h-12 w-12 animate-spin text-terracotta mb-4" />
                  <p className="text-gray-600 font-medium">Generating your personalized YUMMY Declaration...</p>
                  <p className="text-sm text-gray-500 mt-2">Based on your values, vision, and goals</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-terracotta/10 to-amber-50 rounded-xl p-6 border border-terracotta/20">
                    <p className="text-xs text-terracotta font-medium mb-2 uppercase tracking-wide">Your Ultimate Most Meaningful Yet</p>
                    {editingDeclaration ? (
                      <Textarea
                        value={yummyDeclaration}
                        onChange={(e) => setYummyDeclaration(e.target.value)}
                        rows={8}
                        className="bg-white/80 border-terracotta/30 focus:border-terracotta"
                        placeholder="Write your YUMMY Declaration..."
                        data-testid="yummy-declaration-textarea"
                      />
                    ) : (
                      <p className="text-gray-800 leading-relaxed whitespace-pre-wrap text-lg">
                        {yummyDeclaration}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {!editingDeclaration ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingDeclaration(true)}
                          data-testid="edit-declaration-btn"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={generateYummyDeclaration}
                          data-testid="regenerate-declaration-btn"
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Regenerate
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingDeclaration(false)}
                      >
                        Done Editing
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {!generatingDeclaration && (
              <DialogFooter className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => saveDeclaration(false)}
                  className="flex-1"
                >
                  Save Declaration
                </Button>
                <Button
                  onClick={() => saveDeclaration(true)}
                  className="flex-1 bg-forest hover:bg-forest/90 text-white"
                  disabled={addedToDashboard}
                  data-testid="add-to-dashboard-btn"
                >
                  {addedToDashboard ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Added to Dashboard
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Save & Add to Dashboard
                    </>
                  )}
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>

        {/* Milestone Add/Edit Dialog */}
        <Dialog open={showMilestoneDialog} onOpenChange={setShowMilestoneDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Target className="h-5 w-5" />
                {editingMilestone ? 'Edit Milestone' : 'Add New Milestone'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              {/* Goal */}
              <div className="space-y-2">
                <Label htmlFor="milestone-goal">Goal / Milestone *</Label>
                <Textarea
                  id="milestone-goal"
                  placeholder="What do you want to achieve? Be specific and measurable."
                  value={milestoneForm.goal}
                  onChange={(e) => setMilestoneForm({...milestoneForm, goal: e.target.value})}
                  rows={3}
                  spellCheck={true}
                  data-testid="milestone-goal-input"
                />
              </div>
              
              {/* Domain Selection */}
              <div className="space-y-2">
                <Label>Life Domain (Optional)</Label>
                <Select
                  value={milestoneForm.domain_id || 'none'}
                  onValueChange={(value) => setMilestoneForm({...milestoneForm, domain_id: value === 'none' ? '' : value})}
                >
                  <SelectTrigger data-testid="milestone-domain-select">
                    <SelectValue placeholder="Select a life domain" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific domain</SelectItem>
                    {domains.map(domain => (
                      <SelectItem key={domain.id} value={domain.id}>
                        {domain.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Target Date & Priority Row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="milestone-date">Target Date</Label>
                  <Input
                    id="milestone-date"
                    type="date"
                    value={milestoneForm.target_date}
                    onChange={(e) => setMilestoneForm({...milestoneForm, target_date: e.target.value})}
                    data-testid="milestone-date-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={milestoneForm.priority}
                    onValueChange={(value) => setMilestoneForm({...milestoneForm, priority: value})}
                  >
                    <SelectTrigger data-testid="milestone-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">🔥 High</SelectItem>
                      <SelectItem value="medium">⚡ Medium</SelectItem>
                      <SelectItem value="low">🌱 Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Delegation Section */}
              <div className="p-4 bg-sand/30 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="delegatable" className="text-sm font-medium">Can someone help?</Label>
                    <p className="text-xs text-gray-500">Delegation is a superpower!</p>
                  </div>
                  <Switch
                    id="delegatable"
                    checked={milestoneForm.is_delegatable}
                    onCheckedChange={(checked) => setMilestoneForm({...milestoneForm, is_delegatable: checked})}
                  />
                </div>
                
                {milestoneForm.is_delegatable && (
                  <div className="space-y-2">
                    <Label htmlFor="who-can-help">Who can help?</Label>
                    <Input
                      id="who-can-help"
                      placeholder="Name, role, or organization"
                      value={milestoneForm.who_can_help}
                      onChange={(e) => setMilestoneForm({...milestoneForm, who_can_help: e.target.value})}
                      data-testid="milestone-helper-input"
                    />
                  </div>
                )}
              </div>
              
              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="milestone-notes">Notes (Optional)</Label>
                <Textarea
                  id="milestone-notes"
                  placeholder="Any additional context or action steps..."
                  value={milestoneForm.notes}
                  onChange={(e) => setMilestoneForm({...milestoneForm, notes: e.target.value})}
                  rows={2}
                  spellCheck={true}
                  data-testid="milestone-notes-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={closeMilestoneDialog}>
                Cancel
              </Button>
              <Button 
                onClick={saveMilestone}
                disabled={saving || !milestoneForm.goal.trim()}
                className="bg-forest hover:bg-forest/90 text-white"
                data-testid="save-milestone-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingMilestone ? 'Update' : 'Add Milestone')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* SHE Suggestion Dialog - Shows AI refinement as editable suggestion */}
        <Dialog open={showSheSuggestion} onOpenChange={setShowSheSuggestion}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-terracotta" />
                SHE's Suggestion
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <p className="text-sm text-gray-600">
                Here's how SHE refined your {suggestionType === 'legacy' ? 'legacy statement' : '10-year vision'}. 
                Feel free to edit it below before accepting, or dismiss to keep your original.
              </p>
              
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-xs text-purple-600 mb-2 font-medium flex items-center gap-1">
                  <Wand2 className="h-3 w-3" />
                  Edit SHE's suggestion as needed:
                </p>
                <Textarea
                  value={sheSuggestion}
                  onChange={(e) => setSheSuggestion(e.target.value)}
                  rows={10}
                  className="text-base bg-white"
                  spellCheck={true}
                  data-testid="she-suggestion-textarea"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowSheSuggestion(false);
                  setSheSuggestion('');
                  setSuggestionType('');
                }}
              >
                Dismiss
              </Button>
              <Button 
                onClick={acceptSheSuggestion}
                className="bg-terracotta hover:bg-terracotta/90 text-white"
                data-testid="accept-she-suggestion-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Use This
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default LegacyVisionPage;
